import { MatchAnalyticsAggregator } from '../../analytics/contract';
import type { GameAudio } from '../../audio/audio';
import type { BotStrategySnapshot } from '../../bot/bot';
import type { ContentSnapshot, BattleEvent, BattleRules, CardId, CommandRejectReason, PlayerId, WorldPoint } from '../../engine/types';
import { BattleEngine } from '../../engine/engine';
import { createDiagnosticBundle } from '../../diagnostics/analyzer';
import { elixirStageAt } from '../../engine/battle-rules';
import type { I18nPort } from '../../i18n/i18n-service';
import type { MessageKey } from '../../i18n/message-keys';
import { CARD_IMAGES } from '../../renderer/assets';
import type { GameRenderer } from '../../renderer/game-renderer';
import type { MatchContext } from '../match-context';
import { requireElement } from '../dom';
import type { BattleDriver } from '../drivers/battle-driver';
import type { ToastController } from './toast-controller';
import { saveDiagnosticRecoveryBundle } from '../../storage/diagnostics-store';

export type MatchControllerState =
  | Readonly<{ phase: 'idle' }>
  | Readonly<{ phase: 'starting'; context: MatchContext }>
  | Readonly<{ phase: 'running'; context: MatchContext }>
  | Readonly<{ phase: 'paused'; context: MatchContext }>
  | Readonly<{ phase: 'finishing'; context: MatchContext }>;

export interface MatchStartSpec {
  readonly context: MatchContext;
  readonly seed: number;
  readonly arenaId: string;
  readonly decks: Readonly<Record<PlayerId, readonly CardId[]>>;
  readonly rules: BattleRules;
  readonly diagnosticProfile: 'off' | 'standard' | 'full';
  readonly botStrategy?: BotStrategySnapshot;
  readonly createDriver: (engine: BattleEngine) => BattleDriver;
  readonly afterEngineCreated?: (engine: BattleEngine) => void;
}

export interface CompletedMatchRuntime {
  readonly engine: BattleEngine;
  readonly context: MatchContext;
  readonly driverKind: BattleDriver['kind'];
  readonly startedAt: string;
  readonly analytics: ReturnType<MatchAnalyticsAggregator['snapshot']>;
  readonly driverDebug: unknown | null;
  readonly diagnosticsSettled: Promise<void>;
}

export interface MatchControllerOptions {
  readonly content: ContentSnapshot;
  readonly i18n: I18nPort;
  readonly audio: GameAudio;
  readonly toast: ToastController;
  readonly fastMode: boolean;
  readonly onFinished: (runtime: CompletedMatchRuntime) => void;
  readonly onPauseChanged: (paused: boolean) => void;
}

export class MatchController {
  private readonly gameHost = requireElement<HTMLElement>('#gameHost');
  private readonly hand = requireElement<HTMLElement>('#hand');
  private readonly nextCard = requireElement<HTMLImageElement>('#nextCard');
  private readonly timer = requireElement<HTMLElement>('#timer');
  private readonly phaseLabel = requireElement<HTMLElement>('#phase');
  private readonly localCrowns = requireElement<HTMLElement>('#playerCrowns');
  private readonly opponentCrowns = requireElement<HTMLElement>('#botCrowns');
  private readonly localName = requireElement<HTMLElement>('#playerNameLabel');
  private readonly opponentName = requireElement<HTMLElement>('#opponentNameLabel');
  private readonly elixirFill = requireElement<HTMLElement>('#elixirFill');
  private readonly elixirValue = requireElement<HTMLElement>('#elixirValue');
  private readonly countdown = requireElement<HTMLElement>('#countdown');
  private readonly pauseButton = requireElement<HTMLButtonElement>('#pauseButton');
  private readonly debugPanel = requireElement<HTMLElement>('#debugPanel');
  private readonly debugText = requireElement<HTMLElement>('#debugText');
  private renderer: GameRenderer | null = null;
  private rendererInitialization: Promise<GameRenderer> | null = null;
  private engineValue: BattleEngine | null = null;
  private driver: BattleDriver | null = null;
  private rules: BattleRules | null = null;
  private matchStartedAt = '';
  private matchAnalytics: MatchAnalyticsAggregator | null = null;
  private controllerState: MatchControllerState = { phase: 'idle' };
  private selectedCard: CardId | null = null;
  private draggingCard: CardId | null = null;
  private lastEvents: readonly BattleEvent[] = [];
  private handRenderKey = '';
  private debugOpen = false;
  private selectedEntityId: number | null = null;
  private measuredFps = 0;
  private fpsFrames = 0;
  private fpsWindowStart = performance.now();
  private lastTime = performance.now();
  private accumulator = 0;
  private frameDropBurst: { count: number; maxFrameMs: number; totalFrameMs: number; firstTick: number; lastTick: number } | null = null;
  private lastFrameDropFlush = performance.now();
  private lastRecoveryAt = 0;
  private recoveryActive: Promise<void> | null = null;
  private recoveryScheduled = false;

  constructor(private readonly options: MatchControllerOptions) {
    this.bindInput();
    this.options.i18n.subscribe(() => { this.renderHand(); this.syncHud(); this.syncParticipantLabels(); });
    requestAnimationFrame((now) => this.frame(now));
  }

  get state(): MatchControllerState { return this.controllerState; }
  get engine(): BattleEngine | null { return this.engineValue; }
  get active(): boolean { return this.controllerState.phase === 'running' || this.controllerState.phase === 'paused' || this.controllerState.phase === 'finishing'; }
  get paused(): boolean { return this.controllerState.phase === 'paused'; }

  unlockAudio(): void { this.options.audio.unlock(); }

  async ensureRenderer(): Promise<GameRenderer> {
    if (this.renderer) return this.renderer;
    if (this.rendererInitialization) return this.rendererInitialization;
    this.rendererInitialization = (async () => {
      const { GameRenderer } = await import('../../renderer/game-renderer');
      const renderer = new GameRenderer(this.gameHost, () => this.engineValue?.journal ?? null);
      await Promise.all([
        renderer.init(),
        ...[...new Set(Object.values(CARD_IMAGES))].map(async (source) => {
          const image = new Image(); image.src = source;
          try { await image.decode(); } catch { /* Renderer reports missing card assets when used. */ }
        }),
      ]);
      this.renderer = renderer;
      return renderer;
    })().catch((error: unknown) => { this.rendererInitialization = null; throw error; });
    return this.rendererInitialization;
  }

  setQuality(quality: 'auto' | 'low' | 'high'): void { this.renderer?.setQuality(quality); }

  async start(spec: MatchStartSpec, quality: 'auto' | 'low' | 'high'): Promise<BattleEngine> {
    this.abandon(false);
    this.controllerState = { phase: 'starting', context: spec.context };
    const startupStartedAt = performance.now();
    const renderer = await this.ensureRenderer();
    renderer.setQuality(quality);
    this.rules = spec.rules;
    this.engineValue = new BattleEngine({
      seed: spec.seed,
      content: this.options.content,
      arenaId: spec.arenaId,
      decks: spec.decks,
      rules: spec.rules,
      diagnosticProfile: spec.diagnosticProfile,
      matchId: spec.context.matchId,
      ...(spec.botStrategy ? { botStrategy: spec.botStrategy } : {}),
      matchMetadata: { mode: spec.context.mode, resultTrust: spec.context.resultTrust, networkMatchId: spec.context.networkMatchId, localSide: spec.context.localSide },
    });
    spec.afterEngineCreated?.(this.engineValue);
    this.driver = spec.createDriver(this.engineValue);
    renderer.setViewer(spec.context.localSide);
    await renderer.setArena(this.engineValue.arenaDefinition);
    renderer.sync(this.engineValue.getView(spec.context.localSide), [], 0);
    this.selectedCard = null;
    this.draggingCard = null;
    this.handRenderKey = '';
    this.lastEvents = [];
    this.accumulator = 0;
    this.frameDropBurst = null;
    this.lastFrameDropFlush = performance.now();
    this.matchStartedAt = new Date().toISOString();
    this.matchAnalytics = this.driver.kind === 'replay' ? null : new MatchAnalyticsAggregator();
    this.lastRecoveryAt = performance.now();
    this.pauseButton.disabled = !this.driver.allowsPause;
    this.pauseButton.textContent = this.options.i18n.t('app.pause');
    this.options.audio.startMusic(this.engineValue.currentTick);
    this.options.audio.play('match-start', this.engineValue.journal, this.engineValue.currentTick);
    this.renderHand();
    this.syncParticipantLabels();
    this.syncHud();
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    this.engineValue.journal.record({ tick: 0, phase: 'performance', source: 'app-startup', code: 'MATCH_STARTUP_READY', payload: { elapsedMs: performance.now() - startupStartedAt, preloadedCards: new Set(Object.values(CARD_IMAGES)).size } });
    this.controllerState = { phase: 'running', context: spec.context };
    this.queueRecoveryCheckpoint();
    return this.engineValue;
  }

  togglePause(): void {
    if (!this.driver?.allowsPause || (this.controllerState.phase !== 'running' && this.controllerState.phase !== 'paused')) return;
    const paused = this.controllerState.phase === 'running';
    this.controllerState = { phase: paused ? 'paused' : 'running', context: this.controllerState.context };
    this.pauseButton.textContent = this.options.i18n.t(paused ? 'app.resume' : 'app.pause');
    if (paused) this.options.audio.stopMusic(); else this.options.audio.startMusic(this.engineValue?.currentTick ?? 0);
    this.options.onPauseChanged(paused);
    this.renderHand();
  }

  abandon(report = true): void {
    if (report) this.flushFrameDropBurst(performance.now());
    this.driver?.dispose();
    this.driver = null;
    this.engineValue = null;
    this.rules = null;
    this.matchAnalytics = null;
    this.recoveryScheduled = false;
    this.controllerState = { phase: 'idle' };
    this.options.audio.stopMusic();
    this.pauseButton.disabled = true;
    this.selectedCard = null;
    this.renderer?.hidePlacement();
  }

  markFinishing(): void {
    if (this.controllerState.phase === 'running' || this.controllerState.phase === 'paused') this.controllerState = { phase: 'finishing', context: this.controllerState.context };
  }

  captureRuntime(): CompletedMatchRuntime | null {
    if (!this.engineValue || !this.driver || this.controllerState.phase === 'idle') return null;
    return {
      engine: this.engineValue,
      context: this.controllerState.context,
      driverKind: this.driver.kind,
      startedAt: this.matchStartedAt,
      analytics: this.matchAnalytics?.snapshot() ?? { commandsAccepted: 0, commandsRejected: 0, phases: [], cardStats: [] },
      driverDebug: this.createDebugSnapshot(this.engineValue, this.driver),
      diagnosticsSettled: this.recoveryActive ?? Promise.resolve(),
    };
  }

  private bindInput(): void {
    this.gameHost.addEventListener('pointermove', (event) => {
      if (!this.engineValue || !this.selectedCard || !this.renderer) return;
      const point = this.renderer.screenToWorld(event.clientX, event.clientY);
      this.renderer.showPlacement(this.resolvedPlacement(this.selectedCard, point), this.isPreviewValid(this.selectedCard, point), this.placementRadius(this.selectedCard));
    });
    this.gameHost.addEventListener('pointerup', (event) => {
      if (!this.engineValue || !this.renderer || this.draggingCard) return;
      const point = this.renderer.screenToWorld(event.clientX, event.clientY);
      if (this.selectedCard) this.playSelected(point);
      else if (this.debugOpen) {
        const nearest = this.engineValue.getView(this.driver?.localSide ?? 'player').entities.slice().sort((left, right) => Math.hypot(left.x - point.x, left.y - point.y) - Math.hypot(right.x - point.x, right.y - point.y) || left.id - right.id)[0];
        this.selectedEntityId = nearest && Math.hypot(nearest.x - point.x, nearest.y - point.y) <= 900 ? nearest.id : null;
        this.syncHud();
      }
    });
    this.gameHost.addEventListener('pointerleave', () => this.renderer?.hidePlacement());
    document.addEventListener('pointermove', (event) => {
      if (!this.engineValue || !this.draggingCard || !this.renderer) return;
      const rect = this.gameHost.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) return;
      const point = this.renderer.screenToWorld(event.clientX, event.clientY);
      this.renderer.showPlacement(this.resolvedPlacement(this.draggingCard, point), this.isPreviewValid(this.draggingCard, point), this.placementRadius(this.draggingCard));
    });
    document.addEventListener('pointerup', (event) => {
      if (!this.engineValue || !this.draggingCard || !this.renderer) return;
      const cardId = this.draggingCard; this.draggingCard = null;
      if (event.target instanceof Element && event.target.closest('.card-button')) return;
      const rect = this.gameHost.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) return;
      this.selectedCard = cardId;
      this.playSelected(this.renderer.screenToWorld(event.clientX, event.clientY));
    });
    window.addEventListener('keydown', (event) => {
      if (!this.engineValue || !this.driver) return;
      if (event.key >= '1' && event.key <= '4') this.selectCard(this.engineValue.state.players[this.driver.localSide].hand[Number(event.key) - 1] ?? null);
      if (event.key === 'Escape') this.selectCard(null);
      if (event.code === 'Space' && this.driver.allowsPause) { event.preventDefault(); this.togglePause(); }
    });
    this.pauseButton.addEventListener('click', () => this.togglePause());
    requireElement<HTMLButtonElement>('#debugButton').addEventListener('click', () => { this.debugOpen = !this.debugOpen; this.debugPanel.hidden = !this.debugOpen; this.syncHud(); });
  }

  private selectCard(cardId: CardId | null): void {
    if (!this.engineValue || !this.driver?.allowsInput || this.paused || this.controllerState.phase !== 'running') return;
    const player = this.engineValue.state.players[this.driver.localSide];
    if (this.engineValue.state.phase === 'countdown' || this.engineValue.currentTick < player.nextCardReadyTick) return;
    this.selectedCard = this.selectedCard === cardId ? null : cardId;
    if (this.selectedCard) this.options.audio.play('card-pick', this.engineValue.journal, this.engineValue.currentTick);
    this.renderer?.hidePlacement();
    this.renderHand();
  }

  private isPreviewValid(cardId: CardId, point: WorldPoint): boolean {
    return Boolean(this.engineValue && this.driver && this.engineValue.nearestValidPlacement(this.driver.localSide, cardId, point) !== null);
  }

  private resolvedPlacement(cardId: CardId, point: WorldPoint): WorldPoint {
    return this.engineValue && this.driver ? this.engineValue.nearestValidPlacement(this.driver.localSide, cardId, point) ?? point : point;
  }

  private placementRadius(cardId: CardId): number {
    const card = this.options.content.cards.get(cardId);
    if (!card) return 360;
    let radius = 0;
    for (const mechanic of card.mechanics) {
      if (mechanic.type === 'area-projectile') radius = Math.max(radius, this.options.content.spells.get(mechanic.spellId)?.radius ?? 0);
      if (mechanic.type === 'spawn-units') for (const group of mechanic.groups) radius = Math.max(radius, group.formationRadius + (this.options.content.units.get(group.unitId)?.radius ?? 0));
    }
    return Math.max(300, radius);
  }

  private playSelected(point: WorldPoint): void {
    if (!this.engineValue || !this.driver || !this.selectedCard || this.paused || !this.driver.allowsInput) return;
    const result = this.driver.submitCard(this.engineValue, this.selectedCard, this.resolvedPlacement(this.selectedCard, point));
    if (!result.ok) this.options.toast.show(this.options.i18n.t(`command.${result.reason}` as MessageKey));
    else this.selectedCard = null;
    this.renderer?.hidePlacement();
    this.renderHand();
  }

  private renderHand(): void {
    if (!this.engineValue || !this.driver) return;
    const state = this.engineValue.state;
    const player = state.players[this.driver.localSide];
    const cooldownActive = state.tick < player.nextCardReadyTick;
    const renderKey = `${this.options.i18n.locale}|${player.hand.join(',')}|${player.queue[0]}|${this.selectedCard ?? ''}|${Math.floor(player.elixirMilli / 1000)}|${state.phase}|${cooldownActive}|${this.controllerState.phase}`;
    if (renderKey === this.handRenderKey) return;
    this.handRenderKey = renderKey;
    this.hand.replaceChildren();
    player.hand.forEach((cardId, index) => {
      const card = this.options.content.cards.get(cardId)!;
      const cardName = this.options.i18n.text(card.nameKey);
      const button = document.createElement('button');
      button.className = `card-button${this.selectedCard === cardId ? ' selected' : ''}`;
      button.dataset.cardId = cardId;
      button.setAttribute('aria-label', this.options.i18n.t('deck.cardAria', { card: cardName, cost: card.cost, selected: '' }));
      button.disabled = player.elixirMilli < card.cost * 1_000 || state.phase === 'countdown' || cooldownActive || this.controllerState.phase !== 'running' || !this.driver!.allowsInput;
      const image = document.createElement('img'); image.src = CARD_IMAGES[cardId] ?? ''; image.alt = cardName; image.draggable = false;
      const cost = document.createElement('b'); cost.textContent = String(card.cost);
      button.append(image, cost);
      button.addEventListener('pointerdown', () => { this.draggingCard = cardId; });
      button.addEventListener('click', () => { this.draggingCard = null; this.selectCard(cardId); });
      button.title = `${index + 1}: ${cardName}`;
      this.hand.append(button);
    });
    this.nextCard.src = CARD_IMAGES[player.queue[0]!] ?? '';
  }

  private syncParticipantLabels(): void {
    if (this.controllerState.phase === 'idle') return;
    const context = this.controllerState.context;
    const opponentSide: PlayerId = context.localSide === 'player' ? 'bot' : 'player';
    this.localName.textContent = context.participants[context.localSide].displayName ?? this.options.i18n.t('profile.placeholder');
    this.opponentName.textContent = context.participants[opponentSide].displayName ?? (context.participants[opponentSide].kind === 'bot' ? this.options.i18n.t('app.bot') : this.options.i18n.t('profile.placeholder'));
  }

  private syncHud(): void {
    if (!this.engineValue || !this.driver || !this.rules) return;
    const state = this.engineValue.state;
    const localSide = this.driver.localSide;
    const opponentSide: PlayerId = localSide === 'player' ? 'bot' : 'player';
    const remaining = state.phase === 'ended' ? 0 : state.phase === 'normal' ? Math.max(0, this.rules.normalTicks - state.phaseTick) : state.phase === 'overtime' ? Math.max(0, this.rules.overtimeTicks - state.phaseTick) : this.rules.normalTicks;
    const seconds = Math.ceil(remaining / this.rules.tickRate);
    const stage = elixirStageAt(this.rules, state.phase, state.phaseTick);
    this.timer.textContent = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
    this.phaseLabel.textContent = this.options.i18n.t(state.phase === 'countdown' ? 'hud.preparation' : state.phase === 'normal' ? 'hud.regulation' : state.phase === 'overtime' ? 'hud.overtime' : 'hud.ended', state.phase === 'normal' ? { multiplier: stage === 'double' ? ' ×2' : '' } : state.phase === 'overtime' ? { multiplier: stage === 'triple' ? 3 : 2 } : {});
    this.localCrowns.textContent = String(state.players[localSide].crowns);
    this.opponentCrowns.textContent = String(state.players[opponentSide].crowns);
    this.elixirValue.textContent = (state.players[localSide].elixirMilli / 1_000).toFixed(1);
    this.elixirFill.style.width = `${state.players[localSide].elixirMilli / 100}%`;
    this.countdown.textContent = state.phase === 'countdown' ? String(Math.max(1, Math.ceil((this.rules.countdownTicks - state.phaseTick) / this.rules.tickRate))) : '';
    if (this.debugOpen) this.debugText.textContent = JSON.stringify({ fps: this.measuredFps, matchId: this.engineValue.journal.matchId, seed: state.seed, tick: state.tick, phase: state.phase, checksum: this.engineValue.checksum(), entities: state.entities.filter((item) => !item.removed).length, projectiles: state.projectiles.length, records: this.engineValue.journal.records.length, droppedRecords: this.engineValue.journal.droppedRecords, selectedCard: this.selectedCard, selectedEntity: this.selectedEntityId === null ? null : state.entities.find((item) => item.id === this.selectedEntityId) ?? null }, null, 2);
  }

  private frame(now: number): void {
    const rawDelta = (now - this.lastTime) / 1_000;
    const delta = Math.min(this.options.fastMode ? 1 : 0.1, rawDelta);
    this.lastTime = now;
    this.fpsFrames += 1;
    if (now - this.fpsWindowStart >= 500) { this.measuredFps = Math.round((this.fpsFrames * 1_000) / (now - this.fpsWindowStart)); this.fpsFrames = 0; this.fpsWindowStart = now; }
    const engine = this.engineValue;
    if (this.controllerState.phase === 'running' && engine && rawDelta > 0.08 && document.visibilityState === 'visible') this.captureFrameDrop(rawDelta, engine);
    if (this.frameDropBurst && now - this.lastFrameDropFlush >= 2_000) this.flushFrameDropBurst(now);
    if (this.controllerState.phase === 'running' && engine && this.driver && this.rules) {
      this.accumulator += delta;
      const frameEvents: BattleEvent[] = [];
      const stepSeconds = 1 / this.rules.tickRate;
      while (this.accumulator >= stepSeconds && this.driver.canAdvance(engine)) {
        this.driver.beforeStep(engine);
        const tickStartedAt = performance.now();
        frameEvents.push(...engine.step());
        this.driver.afterStep(engine);
        const elapsedMs = performance.now() - tickStartedAt;
        const budgetMs = 1_000 / this.rules.tickRate;
        if (elapsedMs > budgetMs) engine.journal.record({ tick: engine.currentTick, phase: 'performance', source: 'app-frame', level: 'warn', code: 'TICK_OVERRUN', payload: { elapsedMs, budgetMs } });
        else if (elapsedMs > 4) engine.journal.record({ tick: engine.currentTick, phase: 'performance', source: 'app-frame', level: 'debug', code: 'TICK_SLOW', payload: { elapsedMs, budgetMs } });
        this.accumulator -= stepSeconds;
        if (engine.hasEnded) break;
      }
      if (!this.driver.canAdvance(engine)) this.accumulator = Math.min(this.accumulator, stepSeconds * 2);
      this.lastEvents = frameEvents;
      this.matchAnalytics?.consume(frameEvents);
      this.renderer?.sync(engine.getView(this.driver.localSide), frameEvents, delta);
      this.options.audio.handle(frameEvents, engine.journal);
      this.renderHand();
      this.syncHud();
      if (now - this.lastRecoveryAt >= 10_000) this.queueRecoveryCheckpoint();
      if (engine.hasEnded && this.controllerState.phase === 'running') {
        const context = this.controllerState.context;
        this.controllerState = { phase: 'finishing', context };
        this.options.onFinished(this.captureRuntime() ?? { engine, context, driverKind: this.driver.kind, startedAt: this.matchStartedAt, analytics: this.matchAnalytics?.snapshot() ?? { commandsAccepted: 0, commandsRejected: 0, phases: [], cardStats: [] }, driverDebug: null, diagnosticsSettled: Promise.resolve() });
      }
    }
    requestAnimationFrame((next) => this.frame(next));
  }

  private captureFrameDrop(rawDelta: number, engine: BattleEngine): void {
    const frameMs = rawDelta * 1_000;
    this.frameDropBurst ??= { count: 0, maxFrameMs: 0, totalFrameMs: 0, firstTick: engine.currentTick, lastTick: engine.currentTick };
    this.frameDropBurst.count += 1;
    this.frameDropBurst.maxFrameMs = Math.max(this.frameDropBurst.maxFrameMs, frameMs);
    this.frameDropBurst.totalFrameMs += frameMs;
    this.frameDropBurst.lastTick = engine.currentTick;
  }

  private flushFrameDropBurst(now: number): void {
    if (!this.engineValue || !this.frameDropBurst) return;
    this.engineValue.journal.record({ tick: this.frameDropBurst.lastTick, phase: 'performance', source: 'app-frame', level: 'warn', code: 'FRAME_DROP_BURST', payload: { ...this.frameDropBurst, averageFrameMs: this.frameDropBurst.totalFrameMs / this.frameDropBurst.count, phase: this.engineValue.state.phase, entities: this.engineValue.state.entities.filter((entity) => !entity.removed).length, projectiles: this.engineValue.state.projectiles.length, visibilityState: document.visibilityState } });
    this.frameDropBurst = null;
    this.lastFrameDropFlush = now;
  }

  private queueRecoveryCheckpoint(): void {
    if (this.recoveryActive || this.recoveryScheduled || !this.engineValue || !this.driver || this.driver.kind === 'replay' || this.controllerState.phase !== 'running') return;
    this.lastRecoveryAt = performance.now();
    this.recoveryScheduled = true;
    const persist = (): void => {
      this.recoveryScheduled = false;
      const engine = this.engineValue;
      const driver = this.driver;
      if (this.recoveryActive || !engine || !driver || driver.kind === 'replay' || this.controllerState.phase !== 'running') return;
      const bundle = createDiagnosticBundle({
        journal: engine.journal,
        replay: structuredClone(engine.replay),
        checkpoints: engine.checkpoints,
        result: engine.state.result,
        engineVersion: engine.version,
        contentVersion: this.options.content.version,
        arenaId: engine.state.arenaId,
        seed: engine.state.seed,
        currentTick: engine.currentTick,
        terminationReason: 'running',
        debug: this.createDebugSnapshot(engine, driver),
      });
      this.recoveryActive = saveDiagnosticRecoveryBundle(bundle)
        .catch(() => { engine.journal.sinkErrors += 1; })
        .finally(() => { this.recoveryActive = null; });
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(() => persist(), { timeout: 2_000 });
    else globalThis.setTimeout(persist, 0);
  }

  private createDebugSnapshot(engine: BattleEngine, driver: BattleDriver): unknown {
    return {
      schemaVersion: 1,
      matchStartedAt: this.matchStartedAt,
      capturedAtMs: Math.round(performance.now()),
      browserState: {
        visibility: document.visibilityState,
        focused: document.hasFocus(),
        online: navigator.onLine,
        measuredFps: this.measuredFps,
      },
      pendingFrameDropBurst: this.frameDropBurst ? { ...this.frameDropBurst } : null,
      engineState: engine.snapshot(),
      driver: driver.debugSnapshot?.(engine) ?? null,
    };
  }
}
